/* ═══════════════════════════════════════════════════════════════
   InvoicesPage — Real invoice management.

   Tracks invoices end-to-end short of money movement. Bookkeepers /
   PMs / admins draft them, send them, mark them paid, and watch the
   AR aging tighten or rot.  PDF export uses jspdf.

   Layout:
     - List view: filter pills (status), search, summary cards
     - Click any row → editor modal (or full-screen on small windows)
     - Editor: client + project picker, line items table (manual + "Pull
       billable hours from period"), totals, lifecycle dates, PDF download

   Permissions:
     - invoices.view_own | view_all to see the page
     - invoices.create / edit / send / mark_paid / void / delete
     - invoices.view_financials gates $ visibility (rare — most viewers
       can see invoice existence but not amounts)
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useMemo } from 'react';
import { SearchableSelect } from '../components/SearchableSelect';
import { Button } from '../components/ui/button';
import { DataTable, Column } from '../components/DataTable';
import { StatusBadge, StatusVariant } from '../components/StatusBadge';
import { Icon } from '../components/ui/Icon';
import { rem } from '../lib/rem';
import { toAddress } from '../routing/address';
import { useAddressSync } from '../routing/useAddressSync';
import { sanitizeProjectName } from './projects/helpers';
import { Plus, X, Download, FileText } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { InvoiceService } from '../services/invoiceService';
import { ProjectService } from '../services/projectService';
import { OrgService } from '../services/orgService';
import { TeamService } from '../services/teamService';
import { TimeService } from '../services/timeService';
import { AdminService } from '../services/adminService';
import { hasPermission } from '../../shared/permissions';
import { activeOnly } from '../lib/archive';
import type {
  Invoice,
  InvoiceLineItem,
  Project,
  Organization,
  HourEntry,
  TeamMember,
  BillingRole,
} from '../../shared/types';
import type { AuthUser } from '../services/authService';

interface InvoicesPageProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser | null;
  /** Optional one-shot filter from a cross-page drill — e.g. clicking
   *  "AR Aging" on a project dashboard lands here filtered to that
   *  project (status='overdue') or with a specific invoice opened. */
  deepLink?: {
    view: 'invoices';
    projectId?: string;
    invoiceId?: string;
    status?: 'overdue' | 'all';
    /** Widgets R2 — "+ New invoice" from the project dashboard opens the
     *  existing New invoice editor directly instead of just filtering. */
    create?: boolean;
  } | null;
  onDeepLinkConsumed?: () => void;
  /** Project Canvas R1: host mounts this page nested inside a project
   *  panel. Presets the project scope on mount and whenever it changes —
   *  the same removable chip the sidebar page uses still lets the user
   *  clear it; nothing is locked. */
  projectId?: string;
  /** Project Canvas R1: page is embedded in a project host rather than the
   *  sidebar shell. Changes no chrome, table, or actions — the page
   *  already fills its container (height: 100%, internal scroll); this
   *  only skips the onDeepLinkConsumed call, which assumes an app-shell
   *  deep-link slot. */
  embedded?: boolean;
}

// ─── Status palette ─────────────────────────────────────────────────

const STATUS_META: Record<string, { label: string; bg: string; color: string }> = {
  draft: { label: 'Draft', bg: 'var(--sa)', color: 'var(--tm)' },
  sent: {
    label: 'Sent',
    bg: 'color-mix(in srgb, var(--color-info) 12%, transparent)',
    color: 'var(--color-info)',
  },
  partial: {
    label: 'Partial',
    bg: 'color-mix(in srgb, var(--color-warning) 14%, transparent)',
    color: 'var(--color-warning)',
  },
  paid: {
    label: 'Paid',
    bg: 'color-mix(in srgb, var(--ok) 12%, transparent)',
    color: 'var(--ok)',
  },
  void: {
    label: 'Void',
    bg: 'color-mix(in srgb, var(--dg) 10%, transparent)',
    color: 'var(--dg)',
  },
};

// Shared StatusBadge doesn't know invoice-specific statuses (`partial`,
// `void`) by the label/color this page always used — override just those
// two so the list-view badge matches the editor-modal pill exactly.
function statusBadgeOverride(status: string): { label?: string; variant?: StatusVariant } {
  if (status === 'partial') return { label: 'Partial', variant: 'warning' };
  if (status === 'void') return { variant: 'danger' };
  return {};
}

const fmtMoney = (n: number) =>
  '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

// ─── Page ────────────────────────────────────────────────────────────

export default function InvoicesPage({
  toast,
  authUser,
  deepLink,
  onDeepLinkConsumed,
  projectId,
  embedded,
}: InvoicesPageProps) {
  const perms = authUser?.permissions || [];
  const canViewAll = hasPermission(perms, 'invoices.view_all');
  const canViewMoney = hasPermission(perms, 'invoices.view_financials');
  const canCreate = hasPermission(perms, 'invoices.create');
  const canEdit = hasPermission(perms, 'invoices.edit');
  const canSend = hasPermission(perms, 'invoices.send');
  const canMarkPaid = hasPermission(perms, 'invoices.mark_paid');
  const canVoid = hasPermission(perms, 'invoices.void');
  const canDelete = hasPermission(perms, 'invoices.delete');
  const canExportPdf = hasPermission(perms, 'invoices.export_pdf');
  const canArchive = hasPermission(perms, 'invoices.archive');

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Invoice | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | 'all'>('all');
  // Optional project scope set by deep-link drill (sticky until cleared by
  // the user or a different deep-link). Visible as a chip in the toolbar.
  const [projectFilterId, setProjectFilterId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  // Pending invoiceId to open once data has loaded (deep-link race fix).
  const [pendingOpenInvoiceId, setPendingOpenInvoiceId] = useState<string | null>(null);

  // Consume deep-link once. Note we run on every deepLink change, not
  // every render, so a fresh drill from the dashboard re-applies even
  // if the user already navigated within the page.
  useEffect(() => {
    if (!deepLink) return;
    if (deepLink.projectId) setProjectFilterId(deepLink.projectId);
    if (deepLink.status === 'overdue') {
      setStatusFilter('sent');
    } else if (deepLink.status === 'all') {
      setStatusFilter('all');
    }
    if (deepLink.invoiceId) setPendingOpenInvoiceId(deepLink.invoiceId);
    if (deepLink.create) void createNew(deepLink.projectId);
    // Embedded hosts (Project Canvas) own their own deep-link slot — the
    // onDeepLinkConsumed callback assumes the app shell's one-shot pattern.
    if (!embedded) onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  // Project Canvas: host-provided project scope presets the filter — same
  // removable chip as the sidebar page, so the user can still clear it.
  useEffect(() => {
    if (projectId) setProjectFilterId(projectId);
  }, [projectId]);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const [invR, pR, oR, mR, brR] = await Promise.allSettled([
        InvoiceService.list(),
        ProjectService.list(),
        OrgService.list(),
        TeamService.list(),
        AdminService.billingRoles.list().catch(() => [] as BillingRole[]),
      ]);
      if (invR.status === 'fulfilled') setInvoices(invR.value);
      if (pR.status === 'fulfilled') setProjects(pR.value);
      if (oR.status === 'fulfilled') setOrgs(oR.value);
      if (mR.status === 'fulfilled') setMembers(mR.value);
      if (brR.status === 'fulfilled') setBillingRoles(brR.value);
      const rejected = [invR, pR, oR, mR, brR].filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected'
      );
      if (rejected.length > 0) {
        console.error('[InvoicesPage] partial load failure:', rejected);
        const msg = 'Some invoice data failed to load. Check your connection and try again.';
        setLoadError(msg);
        toast(msg, true);
      }
    } catch (err: any) {
      console.error('[InvoicesPage] load failed:', err);
      const msg = 'Load failed: ' + (err?.message || String(err));
      setLoadError(msg);
      toast(msg, true);
    }
    setLoading(false);
  }

  async function doArchiveInvoices(targets: Invoice[], archived: boolean) {
    if (!targets.length) return;
    const who = authUser?.displayName || 'Unknown';
    for (const inv of targets) await InvoiceService.setArchived(inv.id, archived, who);
    await load();
    toast(
      `${archived ? 'Archived' : 'Restored'} ${targets.length} invoice${targets.length === 1 ? '' : 's'}`
    );
  }

  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('specforma-sync-complete', h);
    return () => window.removeEventListener('specforma-sync-complete', h);
  }, []);

  const projectName = (id: string) =>
    sanitizeProjectName(projects.find((p) => p.id === id)?.name) || 'Unknown';
  const projectNumber = (id: string) => projects.find((p) => p.id === id)?.projectNumber || '';
  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name || 'Unknown';

  // Visible invoices — apply view scope + status filter + search
  const visibleInvoices = useMemo(() => {
    let list = activeOnly(invoices, showArchived);
    if (!canViewAll) {
      // Limited to projects the user is on — best-effort by checking if the
      // user's id matches projects.pic / projects.pm. Server-side enforcement
      // is the source of truth.
      const myProjectIds = new Set(
        projects
          .filter((p: any) => p.pic === authUser?.teamMemberId || p.pm === authUser?.teamMemberId)
          .map((p) => p.id)
      );
      list = list.filter((i) => myProjectIds.has(i.projectId));
    }
    if (projectFilterId) list = list.filter((i) => i.projectId === projectFilterId);
    if (statusFilter !== 'all') list = list.filter((i) => i.status === statusFilter);
    return list.sort((a, b) =>
      (b.issueDate || b.date || '').localeCompare(a.issueDate || a.date || '')
    );
  }, [invoices, projects, statusFilter, canViewAll, authUser, projectFilterId, showArchived]);

  // ─── DataTable columns — the invoice-number/client/project/dates/status
  // grid used to be a hand-rolled div grid; search/sort/filter now come
  // free from DataTable (Tranche 5 straggler sweep). Column 6 keeps the
  // original quirk where its header label flips to "Status" when $ is
  // hidden but the cell itself still just shows an em dash (no duplicate
  // status readout) — preserved as-is rather than "fixed" mid-migration. */
  const invoiceColumns = useMemo<Column<Invoice>[]>(
    () => [
      {
        key: 'invoiceNumber',
        label: 'Invoice #',
        width: 120,
        minWidth: 90,
        getValue: (i) => i.invoiceNumber || '',
        render: (i) => (
          <span
            style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 'var(--font-size-base)' }}
          >
            {i.invoiceNumber}
          </span>
        ),
      },
      {
        key: 'client',
        label: 'Client',
        width: 180,
        minWidth: 100,
        getValue: (i) => orgName(i.organizationId),
        render: (i) => <span>{orgName(i.organizationId) || '–'}</span>,
      },
      {
        key: 'project',
        label: 'Project',
        width: 240,
        minWidth: 140,
        getValue: (i) => `${projectNumber(i.projectId)} ${projectName(i.projectId)}`.trim(),
        render: (i) => (
          <span>
            {projectNumber(i.projectId) && (
              <span style={{ color: 'var(--tm)', marginRight: rem(6) }}>
                {projectNumber(i.projectId)}
              </span>
            )}
            {projectName(i.projectId)}
          </span>
        ),
      },
      {
        key: 'issued',
        label: 'Issued',
        width: 100,
        minWidth: 80,
        getValue: (i) => i.issueDate || i.date || '',
        render: (i) => (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--tm)' }}>
            {i.issueDate || i.date}
          </span>
        ),
      },
      {
        key: 'due',
        label: 'Due',
        width: 100,
        minWidth: 80,
        getValue: (i) => i.dueDate || '',
        render: (i) => {
          const isPastDue = !!i.dueDate && i.dueDate < todayIso();
          const overdue = isPastDue && i.status !== 'paid' && i.status !== 'void';
          return (
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                color: overdue ? 'var(--dg)' : 'var(--tm)',
                fontWeight: isPastDue && i.status !== 'paid' ? 700 : 500,
              }}
            >
              {i.dueDate || '–'}
            </span>
          );
        },
      },
      {
        key: 'total',
        label: canViewMoney ? 'Total' : 'Status',
        width: 100,
        minWidth: 80,
        sortable: canViewMoney,
        filterType: canViewMoney ? 'number-range' : 'none',
        // Financial gating: when $ visibility is off, never expose the
        // amount via getValue either — a sortable/filterable numeric
        // column would leak relative amounts even with the cell hidden.
        getValue: (i) => (canViewMoney ? i.total || 0 : ''),
        render: (i) => (
          <span
            style={{
              display: 'block',
              textAlign: 'right',
              fontWeight: 600,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {canViewMoney ? fmtMoney(i.total || 0) : '–'}
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        width: 110,
        minWidth: 90,
        getValue: (i) =>
          i.archivedAt ? 'Archived' : (STATUS_META[i.status] || STATUS_META.draft).label,
        render: (i) => {
          if (i.archivedAt) return <StatusBadge status="archived" />;
          const extra = statusBadgeOverride(i.status);
          return <StatusBadge status={i.status} label={extra.label} variant={extra.variant} />;
        },
      },
    ],
    [canViewMoney, orgs, projects]
  );

  // Once invoices are loaded, satisfy a pending deep-link "open invoice X".
  useEffect(() => {
    if (!pendingOpenInvoiceId || invoices.length === 0) return;
    const target = invoices.find((i) => i.id === pendingOpenInvoiceId);
    if (target) setEditing(target);
    setPendingOpenInvoiceId(null);
  }, [pendingOpenInvoiceId, invoices]);

  // Summary cards
  const stats = useMemo(() => {
    const buckets = InvoiceService.ageingBuckets(invoices);
    const totalOpen = buckets.current + buckets.d30 + buckets.d60 + buckets.d90;
    const draftCount = invoices.filter((i) => i.status === 'draft').length;
    const sentCount = invoices.filter((i) => i.status === 'sent' || i.status === 'partial').length;
    const paidThisMonth = invoices
      .filter((i) => {
        if (i.status !== 'paid') return false;
        const paid = i.paidDate || '';
        return paid.slice(0, 7) === todayIso().slice(0, 7);
      })
      .reduce((s, i) => s + (i.total || 0), 0);
    return { totalOpen, buckets, draftCount, sentCount, paidThisMonth };
  }, [invoices]);

  async function createNew(presetProjectId?: string) {
    if (!canCreate) return;
    const nextNum = await InvoiceService.nextInvoiceNumber();
    const today = todayIso();
    const draft: Invoice = {
      id: '',
      projectId: presetProjectId || '',
      organizationId: '',
      invoiceNumber: nextNum,
      date: today,
      issueDate: today,
      dueDate: addDays(today, 30),
      lineItems: [],
      subtotal: 0,
      tax: 0,
      taxRate: 0,
      total: 0,
      amountPaid: 0,
      status: 'draft',
      terms: 'Net 30',
      notes: '',
      createdBy: authUser?.teamMemberId,
      createdAt: '',
      updatedAt: '',
    };
    setEditing(draft);
  }

  // Address: a saved invoice open in the editor; Back from it closes the editor.
  const openInvoice = editing?.id ? invoices.find((i) => i.id === editing.id) : undefined;
  useAddressSync({
    address: openInvoice ? toAddress({ view: 'invoices', invoiceId: openInvoice.id }) : null,
    listPath: '/billing',
    onLeave: () => setEditing(null),
    title: openInvoice?.invoiceNumber ?? null,
  });

  // ─── Render ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ height: '100%', overflow: 'hidden', padding: 'var(--space-5)' }}>
        <InvoicesSkeleton />
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: 'var(--space-5)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div>
          <h2 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, margin: 0 }}>Billing</h2>
          <p
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--tm)',
              margin: `${rem(4)} 0 0`,
            }}
          >
            Track invoices, sent dates, and payments. SpecForma is tracking-only — record actual
            payments here and reconcile against your accounting system.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <Button
            variant={showArchived ? 'secondary' : 'outline'}
            aria-pressed={showArchived}
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </Button>
          {canCreate && (
            <Button onClick={() => createNew()}>
              <Icon icon={Plus} em={1.4545} aria-hidden="true" />
              New invoice
            </Button>
          )}
        </div>
      </div>

      {/* Load error */}
      {loadError && (
        <div
          role="alert"
          style={{
            padding: `${rem(10)} ${rem(14)}`,
            marginBottom: 'var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: 'color-mix(in srgb, var(--dg) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--dg) 30%, transparent)',
            color: 'var(--dg)',
            fontSize: 'var(--font-size-base)',
          }}
        >
          {loadError}
        </div>
      )}

      {/* Summary cards */}
      {canViewMoney && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 'var(--space-3)',
            marginBottom: rem(20),
          }}
        >
          <SummaryCard
            label="OUTSTANDING"
            value={fmtMoney(stats.totalOpen)}
            color="var(--color-info)"
          />
          <SummaryCard
            label="OVER 60 DAYS"
            value={fmtMoney(stats.buckets.d60 + stats.buckets.d90)}
            color={stats.buckets.d60 + stats.buckets.d90 > 0 ? 'var(--dg)' : 'var(--tm)'}
          />
          <SummaryCard
            label="PAID THIS MONTH"
            value={fmtMoney(stats.paidThisMonth)}
            color="var(--ok)"
          />
          <SummaryCard label="DRAFTS" value={String(stats.draftCount)} color="var(--tm)" />
          <SummaryCard
            label="SENT / PARTIAL"
            value={String(stats.sentCount)}
            color="var(--color-info)"
          />
        </div>
      )}

      {/* Filter + search */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {projectFilterId && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: `${rem(5)} ${rem(10)}`,
              borderRadius: rem(14),
              background: 'color-mix(in srgb, var(--color-info) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--color-info) 40%, transparent)',
              color: 'var(--color-info)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
            }}
          >
            <span>Project: {projectName(projectFilterId)}</span>
            <button
              onClick={() => setProjectFilterId(null)}
              title="Clear project filter"
              aria-label="Clear project filter"
              style={{
                width: rem(24),
                height: rem(24),
                borderRadius: '50%',
                border: 'none',
                background: 'color-mix(in srgb, var(--color-info) 20%, transparent)',
                color: 'var(--color-info)',
                cursor: 'pointer',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon icon={X} em={1.0909} aria-hidden="true" />
            </button>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-1)',
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            padding: rem(2),
          }}
        >
          {(['all', 'draft', 'sent', 'partial', 'paid', 'void'] as const).map((s) => {
            const meta = s === 'all' ? { label: 'All', color: 'var(--tx)' } : STATUS_META[s];
            const active = statusFilter === s;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                style={{
                  padding: `${rem(5)} var(--space-3)`,
                  borderRadius: 'var(--radius-sm)',
                  background: active ? 'var(--sf)' : 'transparent',
                  color: active ? meta.color : 'var(--tm)',
                  border: 'none',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  boxShadow: active ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                }}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <DataTable<Invoice>
        columns={invoiceColumns}
        data={visibleInvoices}
        keyField="id"
        storageKey="invoices"
        onRowClick={canEdit ? (inv) => setEditing(inv) : undefined}
        rowHref={canEdit ? (inv) => toAddress({ view: 'invoices', invoiceId: inv.id }) : undefined}
        initialSort={{ key: 'issued', direction: 'desc' }}
        searchPlaceholder="Search invoice #, client, or project..."
        emptyIcon={<Icon icon={FileText} em={0.8} aria-hidden="true" />}
        emptyTitle={
          invoices.length === 0 ? 'No invoices yet' : 'No invoices match the current filters'
        }
        emptyMessage={
          invoices.length === 0
            ? 'Click New invoice to create your first.'
            : 'Try a different status, search term, or clear the project filter.'
        }
        permissions={perms}
        bulkActions={{
          archive: {
            permission: 'invoices.archive',
            onArchive: (items) => doArchiveInvoices(items, true),
            onUndo: (items) => doArchiveInvoices(items, false),
            label: 'Archive',
          },
          delete: {
            permission: 'invoices.delete',
            onDelete: async (items) => {
              for (const inv of items) await InvoiceService.delete(inv.id);
              await load();
            },
          },
          exportCsv: { permission: 'invoices.export_csv', filename: 'invoices' },
          extra: (items, meta) => {
            const archivedOnes = items.filter((i) => i.archivedAt);
            const single = meta?.count === 1 ? items[0] : null;
            return (
              <>
                {single && canExportPdf && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generatePdf(single, projects, orgs)}
                  >
                    <Icon icon={Download} em={1.1} aria-hidden="true" className="mr-1" />
                    Download PDF
                  </Button>
                )}
                {archivedOnes.length > 0 && canArchive && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void doArchiveInvoices(archivedOnes, false)}
                  >
                    Restore {archivedOnes.length}
                  </Button>
                )}
              </>
            );
          },
        }}
      />

      {/* Editor */}
      {editing && (
        <InvoiceEditor
          invoice={editing}
          projects={projects}
          orgs={orgs}
          members={members}
          billingRoles={billingRoles}
          canEdit={canEdit}
          canSend={canSend}
          canMarkPaid={canMarkPaid}
          canVoid={canVoid}
          canDelete={canDelete}
          canViewMoney={canViewMoney}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
            toast('Invoice saved');
          }}
          onDeleted={async () => {
            setEditing(null);
            await load();
            toast('Invoice deleted');
          }}
          toast={toast}
        />
      )}
    </div>
  );
}

// ─── Loading skeleton ────────────────────────────────────────────────

/** Shown during the first data load (skeletons over spinners — DESIGN.md §4). */
function InvoicesSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading invoices"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 'var(--space-4)' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            height: rem(24),
            width: rem(160),
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
        <div
          style={{
            height: rem(36),
            width: rem(130),
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            style={{
              height: rem(66),
              background: 'var(--sa)',
              borderRadius: 'var(--radius-lg)',
              animation: 'pulse 1.4s ease-in-out infinite',
            }}
          />
        ))}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            style={{
              height: rem(38),
              background: 'var(--sa)',
              borderRadius: 'var(--radius-md)',
              opacity: Math.max(0.25, 0.95 - i * 0.08),
              animation: 'pulse 1.4s ease-in-out infinite',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Summary card ────────────────────────────────────────────────────

function SummaryCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div
      style={{
        background: 'var(--sf)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--bd)',
        padding: `${rem(14)} ${rem(16)}`,
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--tm)',
          fontWeight: 700,
          marginBottom: rem(6),
          letterSpacing: '.05em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 800,
          fontFamily: "'JetBrains Mono', monospace",
          color,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ─── Editor modal ────────────────────────────────────────────────────

function InvoiceEditor({
  invoice,
  projects,
  orgs,
  members,
  billingRoles,
  canEdit,
  canSend,
  canMarkPaid,
  canVoid,
  canDelete,
  canViewMoney,
  onClose,
  onSaved,
  onDeleted,
  toast,
}: {
  invoice: Invoice;
  projects: Project[];
  orgs: Organization[];
  members: TeamMember[];
  billingRoles: BillingRole[];
  canEdit: boolean;
  canSend: boolean;
  canMarkPaid: boolean;
  canVoid: boolean;
  canDelete: boolean;
  canViewMoney: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
  toast: (msg: string, isError?: boolean) => void;
}) {
  const [draft, setDraft] = useState<Invoice>(invoice);
  const [saving, setSaving] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullStart, setPullStart] = useState(addDays(todayIso(), -30));
  const [pullEnd, setPullEnd] = useState(todayIso());
  const [paymentAmount, setPaymentAmount] = useState('');

  // Auto-recompute totals on line item changes.
  useEffect(() => {
    const recomputed = InvoiceService.recompute(draft);
    if (
      recomputed.subtotal !== draft.subtotal ||
      recomputed.tax !== draft.tax ||
      recomputed.total !== draft.total
    ) {
      setDraft(recomputed);
    }
    // Intentionally only watch lineItems + taxRate
  }, [draft.lineItems, draft.taxRate]);

  function update(patch: Partial<Invoice>) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  function updateLineItem(idx: number, patch: Partial<InvoiceLineItem>) {
    setDraft((d) => {
      const next = [...d.lineItems];
      const item = { ...next[idx], ...patch };
      // Auto-recalc amount when hours or rate changes
      const hours = item.hours ?? item.quantity ?? 0;
      const rate = item.rate ?? 0;
      item.amount = Math.round(hours * rate * 100) / 100;
      item.quantity = hours;
      next[idx] = item;
      return { ...d, lineItems: next };
    });
  }

  function addManualLine() {
    setDraft((d) => ({
      ...d,
      lineItems: [
        ...d.lineItems,
        {
          description: '',
          hours: 0,
          quantity: 0,
          rate: 0,
          amount: 0,
          sourceKind: 'manual' as const,
        },
      ],
    }));
  }

  function removeLine(idx: number) {
    setDraft((d) => ({ ...d, lineItems: d.lineItems.filter((_, i) => i !== idx) }));
  }

  async function pullBillableHours() {
    if (!draft.projectId) {
      toast('Pick a project first', true);
      return;
    }
    setPulling(true);
    try {
      const entries = await TimeService.listEntries(draft.projectId, pullStart, pullEnd);
      // Skip entries already invoiced (have invoice_id) or non-billable
      const billable = entries.filter(
        (e) => (e as any).billable !== false && !(e as any).invoiceId
      );
      if (billable.length === 0) {
        toast('No new billable hours in that range', true);
        return;
      }
      const rateFor = (e: HourEntry) => {
        const m = members.find((mm) => mm.id === e.teamMemberId);
        if (!m) return 0;
        const role = billingRoles.find((r) => r.id === m.billingRoleId);
        return m.hourlyRate ?? role?.defaultRate ?? 0;
      };
      const descFor = (e: HourEntry) => {
        const m = members.find((mm) => mm.id === e.teamMemberId);
        const role = billingRoles.find((r) => r.id === m?.billingRoleId);
        return role ? role.name : m?.name || 'Time';
      };
      const newLines = InvoiceService.lineItemsFromTimeEntries(billable, rateFor, descFor);
      setDraft((d) => ({
        ...d,
        lineItems: [...d.lineItems, ...newLines],
      }));
      toast(`Added ${newLines.length} line items from ${billable.length} hour entries`);
    } catch (err: any) {
      toast('Pull failed: ' + (err?.message || String(err)), true);
    } finally {
      setPulling(false);
    }
  }

  async function save() {
    if (!draft.projectId || !draft.organizationId) {
      toast('Pick a client and project first', true);
      return;
    }
    setSaving(true);
    try {
      await InvoiceService.save(draft);
      onSaved();
    } catch (err: any) {
      toast('Save failed: ' + (err?.message || String(err)), true);
    } finally {
      setSaving(false);
    }
  }

  async function markSent() {
    if (!canSend) return;
    const next: Invoice = { ...draft, status: 'sent', sentDate: todayIso() };
    setDraft(next);
    setSaving(true);
    try {
      await InvoiceService.save(next);
      onSaved();
    } catch (err: any) {
      toast('Save failed: ' + (err?.message || String(err)), true);
    } finally {
      setSaving(false);
    }
  }

  async function recordPayment() {
    if (!canMarkPaid) return;
    const amt = parseFloat(paymentAmount);
    if (!isFinite(amt) || amt <= 0) {
      toast('Enter a payment amount', true);
      return;
    }
    const newPaid = (draft.amountPaid || 0) + amt;
    const isFull = newPaid >= (draft.total || 0);
    const next: Invoice = {
      ...draft,
      amountPaid: Math.min(newPaid, draft.total || 0),
      status: isFull ? 'paid' : 'partial',
      paidDate: isFull ? todayIso() : draft.paidDate,
    };
    setDraft(next);
    setPaymentAmount('');
    setSaving(true);
    try {
      await InvoiceService.save(next);
      onSaved();
    } catch (err: any) {
      toast('Save failed: ' + (err?.message || String(err)), true);
    } finally {
      setSaving(false);
    }
  }

  async function voidInvoice() {
    if (!canVoid) return;
    if (!confirm('Void this invoice? It will remain visible but excluded from AR.')) return;
    const next: Invoice = { ...draft, status: 'void' };
    setSaving(true);
    try {
      await InvoiceService.save(next);
      onSaved();
    } catch (err: any) {
      toast('Save failed: ' + (err?.message || String(err)), true);
    } finally {
      setSaving(false);
    }
  }

  async function deleteInvoice() {
    if (!canDelete || !draft.id) return;
    if (!confirm('DELETE this invoice permanently? Use Void to keep an audit trail instead.'))
      return;
    setSaving(true);
    try {
      await InvoiceService.delete(draft.id);
      onDeleted();
    } catch (err: any) {
      toast('Delete failed: ' + (err?.message || String(err)), true);
    } finally {
      setSaving(false);
    }
  }

  const meta = STATUS_META[draft.status] || STATUS_META.draft;
  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name || '';
  const projectsForOrg = draft.organizationId
    ? projects.filter(
        (p) =>
          (p as any).organizationId === draft.organizationId ||
          (p as any).clientOrgId === draft.organizationId
      )
    : projects;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--bd)',
          borderRadius: rem(12),
          width: `min(${rem(900)}, 95vw)`,
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: `${rem(16)} ${rem(20)}`,
            borderBottom: '1px solid var(--bd)',
            gap: 'var(--space-3)',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
              {draft.id ? 'Edit Invoice' : 'New Invoice'}
            </h3>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--tm)',
                marginTop: rem(4),
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
              }}
            >
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {draft.invoiceNumber}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  padding: `${rem(2)} var(--space-2)`,
                  borderRadius: rem(999),
                  background: meta.bg,
                  color: meta.color,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                {meta.label}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <Icon icon={X} em={1.5385} aria-hidden="true" />
          </Button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: rem(20),
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)',
          }}
        >
          {/* Top fields */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 140px 140px',
              gap: 'var(--space-3)',
            }}
          >
            <Field label="Client">
              <SearchableSelect
                value={draft.organizationId}
                options={orgs.map((o) => ({ value: o.id, label: o.name }))}
                onChange={(v) => update({ organizationId: v })}
                disabled={!canEdit}
                placeholder="-- Pick client --"
                searchPlaceholder="Search clients…"
                style={inputStyle}
              />
            </Field>
            <Field label="Project">
              <SearchableSelect
                value={draft.projectId}
                options={projectsForOrg.map((p) => ({
                  value: p.id,
                  label: p.name,
                  hint: p.projectNumber || undefined,
                }))}
                onChange={(v) => update({ projectId: v })}
                disabled={!canEdit}
                placeholder="-- Pick project --"
                searchPlaceholder="Search projects…"
                style={inputStyle}
              />
            </Field>
            <Field label="Issue Date">
              <input
                type="date"
                value={draft.issueDate || draft.date || ''}
                onChange={(e) => update({ issueDate: e.target.value, date: e.target.value })}
                disabled={!canEdit}
                style={inputStyle}
              />
            </Field>
            <Field label="Due Date">
              <input
                type="date"
                value={draft.dueDate || ''}
                onChange={(e) => update({ dueDate: e.target.value })}
                disabled={!canEdit}
                style={inputStyle}
              />
            </Field>
          </div>

          {/* Pull from time + add manual */}
          {canEdit && (
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'center',
                padding: `${rem(10)} var(--space-3)`,
                background: 'var(--sa)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              <span style={{ color: 'var(--tm)', fontWeight: 600 }}>Pull billable hours:</span>
              <input
                type="date"
                value={pullStart}
                onChange={(e) => setPullStart(e.target.value)}
                aria-label="Pull start date"
                style={{
                  ...inputStyle,
                  padding: `var(--space-1) var(--space-2)`,
                  width: rem(130),
                  fontSize: 'var(--font-size-sm)',
                }}
              />
              <span style={{ color: 'var(--tm)' }}>to</span>
              <input
                type="date"
                value={pullEnd}
                onChange={(e) => setPullEnd(e.target.value)}
                aria-label="Pull end date"
                style={{
                  ...inputStyle,
                  padding: `var(--space-1) var(--space-2)`,
                  width: rem(130),
                  fontSize: 'var(--font-size-sm)',
                }}
              />
              <button
                onClick={pullBillableHours}
                disabled={pulling || !draft.projectId}
                style={{
                  padding: `${rem(5)} var(--space-3)`,
                  borderRadius: rem(5),
                  border: '1px solid var(--ac)',
                  background: 'transparent',
                  color: 'var(--ac)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: pulling ? 'wait' : 'pointer',
                  opacity: !draft.projectId ? 0.4 : 1,
                }}
              >
                {pulling ? 'Pulling…' : 'Pull'}
              </button>
              <span style={{ flex: 1 }} />
              <button
                onClick={addManualLine}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  padding: `${rem(5)} var(--space-3)`,
                  borderRadius: rem(5),
                  border: '1px dashed var(--bd)',
                  background: 'transparent',
                  color: 'var(--tx)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Icon icon={Plus} em={1.0909} aria-hidden="true" />
                Add manual line
              </button>
            </div>
          )}

          {/* Line items */}
          <div
            style={{
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 100px 110px 30px',
                gap: 'var(--space-2)',
                padding: `var(--space-2) var(--space-3)`,
                background: 'var(--sa)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                color: 'var(--tm)',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
              }}
            >
              <span>Description</span>
              <span style={{ textAlign: 'right' }}>Hours</span>
              <span style={{ textAlign: 'right' }}>Rate</span>
              <span style={{ textAlign: 'right' }}>Amount</span>
              <span></span>
            </div>
            {draft.lineItems.length === 0 && (
              <div
                style={{
                  padding: 'var(--space-5)',
                  textAlign: 'center',
                  color: 'var(--tm)',
                  fontSize: 'var(--font-size-base)',
                }}
              >
                No line items yet. Pull billable hours from time tracking, or add manual lines.
              </div>
            )}
            {draft.lineItems.map((li, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 100px 110px 30px',
                  gap: 'var(--space-2)',
                  padding: `${rem(6)} var(--space-3)`,
                  borderTop: '1px solid var(--bd)',
                  alignItems: 'center',
                }}
              >
                <input
                  value={li.description}
                  onChange={(e) => updateLineItem(idx, { description: e.target.value })}
                  disabled={!canEdit}
                  placeholder="e.g. Engineering time, Sept 2026"
                  aria-label={`Description for line ${idx + 1}`}
                  style={{ ...inputStyle, padding: `${rem(5)} var(--space-2)` }}
                />
                <input
                  type="number"
                  step="0.25"
                  value={li.hours || 0}
                  onChange={(e) => updateLineItem(idx, { hours: parseFloat(e.target.value) || 0 })}
                  disabled={!canEdit}
                  aria-label={`Hours for line ${idx + 1}`}
                  style={{
                    ...inputStyle,
                    padding: `${rem(5)} var(--space-2)`,
                    textAlign: 'right',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                />
                <input
                  type="number"
                  step="0.01"
                  value={li.rate || 0}
                  onChange={(e) => updateLineItem(idx, { rate: parseFloat(e.target.value) || 0 })}
                  disabled={!canEdit}
                  aria-label={`Rate for line ${idx + 1}`}
                  style={{
                    ...inputStyle,
                    padding: `${rem(5)} var(--space-2)`,
                    textAlign: 'right',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                />
                <span
                  style={{
                    textAlign: 'right',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 600,
                    color: 'var(--tx)',
                  }}
                >
                  {fmtMoney(li.amount || 0)}
                </span>
                {canEdit && (
                  <button
                    onClick={() => removeLine(idx)}
                    aria-label={`Remove line ${idx + 1}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--dg)',
                      cursor: 'pointer',
                      padding: rem(5),
                      borderRadius: 'var(--radius-sm)',
                    }}
                    title="Remove line"
                  >
                    <Icon icon={X} em={1.0769} aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Totals */}
          {canViewMoney && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ minWidth: rem(280), fontSize: 'var(--font-size-base)' }}>
                <Row label="Subtotal" value={fmtMoney(draft.subtotal || 0)} />
                <Row
                  label={
                    <span>
                      Tax (
                      <input
                        type="number"
                        step="0.1"
                        value={draft.taxRate || 0}
                        onChange={(e) => update({ taxRate: parseFloat(e.target.value) || 0 })}
                        disabled={!canEdit}
                        aria-label="Tax rate percent"
                        style={{
                          width: rem(50),
                          padding: `${rem(2)} var(--space-1)`,
                          fontSize: 'var(--font-size-sm)',
                          background: 'var(--ib)',
                          border: '1px solid var(--bd)',
                          borderRadius: rem(3),
                          color: 'var(--tx)',
                        }}
                      />
                      %)
                    </span>
                  }
                  value={fmtMoney(draft.tax || 0)}
                />
                <Row label="Total" value={fmtMoney(draft.total || 0)} bold />
                {(draft.amountPaid || 0) > 0 && (
                  <>
                    <Row label="Paid" value={fmtMoney(draft.amountPaid || 0)} color="var(--ok)" />
                    <Row
                      label="Balance"
                      value={fmtMoney((draft.total || 0) - (draft.amountPaid || 0))}
                      bold
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {/* Notes + Terms */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <Field label="Notes">
              <textarea
                value={draft.notes || ''}
                onChange={(e) => update({ notes: e.target.value })}
                disabled={!canEdit}
                placeholder="Internal notes or invoice memo"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>
            <Field label="Terms">
              <textarea
                value={draft.terms || ''}
                onChange={(e) => update({ terms: e.target.value })}
                disabled={!canEdit}
                placeholder="e.g. Net 30, payable to..."
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>
          </div>

          {/* Payment recording (only when sent/partial) */}
          {(draft.status === 'sent' || draft.status === 'partial') && canMarkPaid && (
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'center',
                padding: `${rem(10)} var(--space-3)`,
                background: 'color-mix(in srgb, var(--ok) 6%, transparent)',
                border: '1px solid color-mix(in srgb, var(--ok) 30%, transparent)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <span
                style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--ok)' }}
              >
                Record payment:
              </span>
              <input
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder={`Up to ${fmtMoney((draft.total || 0) - (draft.amountPaid || 0))}`}
                aria-label="Payment amount"
                style={{
                  ...inputStyle,
                  padding: `${rem(5)} ${rem(10)}`,
                  width: rem(180),
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              />
              <button
                onClick={recordPayment}
                style={{
                  padding: `${rem(5)} ${rem(14)}`,
                  borderRadius: rem(5),
                  border: 'none',
                  background: 'var(--ok)',
                  color: 'var(--color-text-inverse)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Record
              </button>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: `${rem(14)} ${rem(20)}`,
            borderTop: '1px solid var(--bd)',
            gap: 'var(--space-2)',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {canDelete && draft.id && (
              <Button variant="destructive" size="sm" onClick={deleteInvoice} disabled={saving}>
                Delete
              </Button>
            )}
            {canVoid && draft.status !== 'void' && draft.id && (
              <Button variant="outline" size="sm" onClick={voidInvoice} disabled={saving}>
                Void
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="outline" size="sm" onClick={() => generatePdf(draft, projects, orgs)}>
              Export PDF
            </Button>
            {draft.status === 'draft' && canSend && draft.id && (
              <Button variant="secondary" size="sm" onClick={markSent} disabled={saving}>
                Mark Sent
              </Button>
            )}
            {canEdit && (
              <Button
                size="sm"
                onClick={save}
                disabled={saving || !draft.projectId || !draft.organizationId}
              >
                {saving ? 'Saving…' : draft.id ? 'Save' : 'Create Invoice'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--tm)',
          marginBottom: 'var(--space-1)',
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function Row({
  label,
  value,
  bold,
  color,
}: {
  label: React.ReactNode;
  value: string;
  bold?: boolean;
  color?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: `var(--space-1) 0`,
        borderTop: bold ? '1px solid var(--bd)' : 'none',
        marginTop: bold ? rem(6) : 0,
        paddingTop: bold ? 'var(--space-2)' : 'var(--space-1)',
        color: color || 'var(--tx)',
        fontWeight: bold ? 700 : 400,
        fontSize: bold ? 'var(--font-size-md)' : 'var(--font-size-base)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>{label}</span>
      <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}

// ─── PDF generator ───────────────────────────────────────────────────
// NOTE: colors/sizes here are jsPDF document styling (points, RGB triples for
// a rendered PDF), not app CSS — the design-token system does not apply to
// generated-document content. Left as-is intentionally.

function generatePdf(invoice: Invoice, projects: Project[], orgs: Organization[]) {
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
  const project = projects.find((p) => p.id === invoice.projectId);
  const org = orgs.find((o) => o.id === invoice.organizationId);

  pdf.setFontSize(20);
  pdf.text('INVOICE', 40, 60);

  pdf.setFontSize(10);
  pdf.setTextColor(100);
  pdf.text(`Invoice #: ${invoice.invoiceNumber}`, 40, 90);
  pdf.text(`Issue Date: ${invoice.issueDate || invoice.date || ''}`, 40, 105);
  if (invoice.dueDate) pdf.text(`Due Date: ${invoice.dueDate}`, 40, 120);

  // Client block
  pdf.setTextColor(40);
  pdf.setFontSize(11);
  pdf.text('Bill To:', 350, 90);
  pdf.setFontSize(12);
  pdf.text(org?.name || 'Unknown Client', 350, 108);
  if (project) {
    pdf.setFontSize(10);
    pdf.setTextColor(100);
    pdf.text(
      `Project: ${project.projectNumber ? `${project.projectNumber} — ` : ''}${project.name}`,
      350,
      124
    );
  }

  // Line items
  autoTable(pdf, {
    startY: 160,
    head: [['Description', 'Hours', 'Rate', 'Amount']],
    body: invoice.lineItems.map((li) => [
      li.description,
      String(li.hours ?? li.quantity ?? 0),
      `$${(li.rate || 0).toFixed(2)}`,
      `$${(li.amount || 0).toFixed(2)}`,
    ]),
    headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
    },
    styles: { fontSize: 10, cellPadding: 6 },
  });

  // Totals
  // @ts-expect-error - autoTable adds finalY to pdf instance
  const totalsY = pdf.lastAutoTable.finalY + 20;
  pdf.setFontSize(10);
  pdf.text(`Subtotal: $${(invoice.subtotal || 0).toFixed(2)}`, 420, totalsY);
  if ((invoice.tax || 0) > 0) {
    pdf.text(
      `Tax (${invoice.taxRate || 0}%): $${(invoice.tax || 0).toFixed(2)}`,
      420,
      totalsY + 16
    );
  }
  pdf.setFontSize(13);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`TOTAL: $${(invoice.total || 0).toFixed(2)}`, 420, totalsY + (invoice.tax ? 38 : 22));

  if (invoice.terms) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text('Terms:', 40, totalsY + 60);
    const termsLines = pdf.splitTextToSize(invoice.terms, 450);
    pdf.text(termsLines, 40, totalsY + 75);
  }

  pdf.save(`${invoice.invoiceNumber || 'invoice'}.pdf`);
}

// ─── Style constants ─────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  padding: `${rem(7)} ${rem(10)}`,
  borderRadius: rem(5),
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  width: '100%',
};
